import { isString } from 'api/helpers';
import { immerable } from 'immer';

import {
    AccountTypeEnum,
    MapViewEnum,
    ProjectAssetEnum,
    SiteWalkViewTypeEnum,
    StatusEnum,
    UserRoleEnum,
    WeatherModeEnum,
} from 'api/enums';
import { IProjectModel, ProjectModel } from './project.model';

/* -------------------------------------------------------------------------------------------------
 * IProjectAssetAccess
 * -----------------------------------------------------------------------------------------------*/
export interface IProjectAssetPermissions {
    [projectId: string]: ProjectAssetEnum[];
}
/* -------------------------------------------------------------------------------------------------
 * IUserModel
 * -----------------------------------------------------------------------------------------------*/
export interface IUserModel {
    _id: string;
    name: string;
    username: string;
    password: string;
    email: string;
    role: UserRoleEnum;
    phone: { number: string; dialCode: string; countryCode: string };
    dob: string;
    dpUrl: string;
    designation?: string;
    status: StatusEnum;
    tags: string[];
    preset: {
        maxDocSize: number;
        totalSpace: number;
        totalSpaceUsed: number;
        color: string;
    };
    preferences: {
        timezone: string;
        primaryColor: string;
        theme: string;
        isEmailNotificationEnabled: boolean;
        show4k: boolean;
        showZoomed: boolean;
        showTimestamp: boolean;
        weatherMode: WeatherModeEnum;
        mapViewType: MapViewEnum;
        siteWalkViewType: SiteWalkViewTypeEnum;
        showPath: boolean;
        pathColor: string;
    };
    lastActive: Date;
    hiddenProjects: string[];
    projectSortOrder: any;
    projectAssetPermissions: IProjectAssetPermissions;
    accountType: AccountTypeEnum;
    expiry: string;

    // Relationships
    createdBy: IUserModel;
    projects: string[] | IProjectModel[];
}

export interface IUserPreference {
    timezone: string;
    primaryColor: string;
    theme: string;
    isEmailNotificationEnabled: boolean;
    show4k: boolean;
    showZoomed: boolean;
    showTimestamp: boolean;
    weatherMode: WeatherModeEnum;
    mapViewType: MapViewEnum;
    siteWalkViewType: SiteWalkViewTypeEnum;
    showPath: boolean;
    pathColor: string;
}

/* -------------------------------------------------------------------------------------------------
 * UserModel
 * -----------------------------------------------------------------------------------------------*/
export class UserModel {
    [immerable] = true;

    _id: string;
    name: string;
    username: string;
    password: string;
    email: string;
    role: UserRoleEnum;
    phone: { number: string; dialCode: string; countryCode: string };
    dob: string;
    dpUrl: string;
    designation?: string;
    status: StatusEnum;
    tags: string[];
    preset: {
        maxDocSize: number;
        totalSpace: number;
        totalSpaceUsed: number;
        color: string;
    };
    preferences: {
        timezone: string;
        primaryColor: string;
        theme: string;
        isEmailNotificationEnabled: boolean;
        show4k: boolean;
        showZoomed: boolean;
        showTimestamp: boolean;
        weatherMode: WeatherModeEnum;
        mapViewType: MapViewEnum;
        siteWalkViewType: SiteWalkViewTypeEnum;
        showPath: boolean;
        pathColor: string;
    };
    lastActive: Date;
    hiddenProjects: string[];
    projectSortOrder: any;
    projectAssetPermissions: IProjectAssetPermissions;
    accountType: AccountTypeEnum;
    expiry: string;

    // Relationships
    createdBy: UserModel;
    projects: string[] | ProjectModel[];

    get hasPhone() {
        return Boolean(this.phone && this.phone.number && this.phone.dialCode && this.phone.countryCode);
    }

    // get permissions() {
    //     return PermissionMap[this.role];
    // }

    constructor(data: Partial<IUserModel>) {
        this._id = data._id ?? '';
        this.name = data.name ?? '';
        this.username = data.username ?? '';
        this.password = data.password ?? '';
        this.email = data.email ?? '';
        this.role = data.role ?? null;
        this.phone = {
            number: data.phone?.number ?? '',
            dialCode: data.phone?.dialCode ?? '',
            countryCode: data.phone?.countryCode ?? '',
        };
        this.dob = data.dob ?? '';
        this.dpUrl = data.dpUrl ?? '';
        this.designation = data.designation ?? '';
        this.status = data.status ?? null;
        this.tags = data.tags ?? [];
        this.preset = {
            maxDocSize: data.preset?.maxDocSize ?? null,
            totalSpace: data.preset?.totalSpace ?? null,
            totalSpaceUsed: data.preset?.totalSpaceUsed ?? null,
            color: data.preset?.color ?? '',
        };
        this.preferences = {
            timezone: data.preferences?.timezone ?? '',
            primaryColor: data.preferences?.primaryColor ?? '',
            theme: data.preferences?.theme ?? '',
            isEmailNotificationEnabled: data.preferences?.isEmailNotificationEnabled ?? null,
            show4k: data.preferences?.show4k ?? null,
            showZoomed: data.preferences?.showZoomed ?? null,
            showTimestamp: data.preferences?.showTimestamp ?? null,
            weatherMode: data.preferences?.weatherMode ?? null,
            mapViewType: data.preferences?.mapViewType ?? null,
            siteWalkViewType: data.preferences?.siteWalkViewType ?? null,
            showPath: data.preferences?.showPath ?? null,
            pathColor: data.preferences?.pathColor ?? null,
        };
        this.lastActive = data.lastActive ?? null;
        this.hiddenProjects = data.hiddenProjects ?? [];
        this.projectSortOrder = data.projectSortOrder ?? {};
        this.projectAssetPermissions = data.projectAssetPermissions ?? {};
        this.accountType = data.accountType ?? null;
        this.expiry = data.expiry ?? '';

        if (data.createdBy) {
            this.createdBy = UserModel.fromObject(data.createdBy);
        }

        if (data.projects && data.projects.length > 0) {
            if (isString(data.projects[0])) {
                this.projects = data.projects as string[];
            } else {
                this.projects = data.projects.map((row) => ProjectModel.fromObject(row as IProjectModel));
            }
        } else {
            this.projects = [];
        }
    }

    static fromObject(data: Partial<IUserModel>): UserModel {
        const model = new UserModel(data);

        return model;
    }

    static fromObjectList(data: Partial<IUserModel>[]): UserModel[] {
        return data.map((row) => UserModel.fromObject(row));
    }

    static cloneFrom(data: Partial<IUserModel>): UserModel {
        const model = new UserModel(data);

        return model;
    }

    toObject(): IUserModel {
        const data: Partial<IUserModel> = {
            _id: this._id,
            name: this.name,
            username: this.username,
            password: this.password,
            email: this.email,
            role: this.role,
            phone: this.hasPhone ? this.phone : null,
            dob: this.dob || null,
            dpUrl: this.dpUrl,
            designation: this.designation || null,
            status: this.status,
            tags: this.tags || [],
            preset: this.preset,
            preferences: this.preferences,
            lastActive: this.lastActive,
            hiddenProjects: this.hiddenProjects,
            projectSortOrder: this.projectSortOrder || {},
            projectAssetPermissions: this.projectAssetPermissions || {},
            accountType: this.accountType,
            expiry: this.expiry,
        };

        if (this.createdBy) {
            data.createdBy = this.createdBy.toObject();
        }

        if (this.projects && this.projects.length > 0) {
            if (this.projects[0] instanceof ProjectModel) {
                data.projects = this.projects.map((row) => (row as ProjectModel).toObject()) as IProjectModel[];
            } else {
                data.projects = this.projects as string[];
            }
        } else {
            data.projects = [];
        }

        return data as IUserModel;
    }
}
